import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import {
  getProductDefaultInfo,
  getCartProductInfo,
  getCartProductAvailabilityInfo,
} from '../../../../utilities/selectors';
import {
  getZemplinProductDefaultInfo,
  getZemplinCartProductInfo,
  getZemplinTrueCount,
} from '../../selectors';
import { OrderNrInput } from '../../Product/OrderNrInput';
import { StockInfo } from '../../Product/StockInfo';
import { ProductPriceCartTable } from '../../Product/ProductPriceCartTable';
import { TableTd, TableRow } from '../../General/Table';
import {
  FlexCol,
  FlexRowCenterHorizontal,
} from '../../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../../Defaults/Text';
import { IconType, SvgIcon } from '../../General/SvgIcon';
import { CountSetter } from '../../ProductDetail/CountSetter';
import { FormInput } from '../../Form/FormInput';
import { DotDelimeter } from '../../General/DotDelimeter';
import { CustomNrStock } from './CustomNrStock';
import {
  getSymbolFromCurrency,
  formatPriceToString,
  round,
} from '../../../../utilities';
import { StoresInfoCartPopup } from '../../Product/StoresInfoCartPopup';

export interface Props {
  product: any;
  addToCart: any;
  removeFromCart: any;
  theme: any;
  hasBottomBorder?: boolean;
  isB2B: boolean;
}

function ItemsTabRow({
  product,
  addToCart,
  theme,
  hasBottomBorder = true,
  removeFromCart,
  isB2B,
}: Props) {
  const [isFocused, setIsFocused] = React.useState(false);

  const {
    currency,
    picture,
    url,
    name,
    ordernr: currentOrdernr,
    productId,
    goodId,
  } = getCartProductInfo(product);
  const {
    unit,
    sumPrice,
    sumPriceWithoutVat,
    count: initialCount,
    productPackages,
    onStock,
    storesInfo,
    finalPricePerUnitWithVat,
    finalPricePerUnitWithoutVat,
    priceWithoutVat,
    price,
  } = getZemplinCartProductInfo(product);
  const isMks = unit === 'Mks';
  const step = productPackages ? productPackages : isMks ? 0.001 : 1;
  const [count, setCount] = React.useState(initialCount.toString());
  const itemCount = isMks ? initialCount * 1000 : initialCount;
  const { color, availText } = getCartProductAvailabilityInfo(
    product,
    itemCount,
    onStock,
  );
  const trueOnStock = isMks ? onStock / 1000 : onStock;
  const availableCount = count > trueOnStock ? trueOnStock : count;

  const correctSumPrice = isB2B ? sumPriceWithoutVat : sumPrice;

  const trueAllPrice = isB2B
    ? priceWithoutVat
      ? round(priceWithoutVat, 2)
      : ''
    : price
    ? round(price, 2)
    : '';

  const priceString =
    onStock > 0
      ? !isNaN(correctSumPrice)
        ? formatPriceToString(correctSumPrice, currency)
        : ''
      : '-';

  const correctPerUnitPrice = round(correctSumPrice / availableCount, 2);
  const perUnitPriceString = !isFocused
    ? onStock > 0
      ? `${correctPerUnitPrice} ${getSymbolFromCurrency(currency)}`
      : '-'
    : '';

  const minAmount = unit === 'Mks' ? 0.001 : 1;
  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.replace(',', '.');
    let newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    if (shouldAddToCart) {
      if (parseFloat(newCount) < minAmount) {
        newCount = minAmount.toFixed(3);
      }
      newCountNumber = parseFloat(newCount);
      const countDiff = -(+initialCount - +newCountNumber);
      const correctCount = getZemplinTrueCount(unit === 'Mks', countDiff);
      addToCart(product, correctCount);
    }

    setCount(newCount);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleRemoveClick = () => {
    removeFromCart(productId, goodId);
  };

  const handleEnterFunction = React.useCallback(event => {
    if (event.keyCode === 13) {
      event.target.blur();
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('keydown', handleEnterFunction, false);

    return () => {
      document.removeEventListener('keydown', handleEnterFunction, false);
    };
  }, []);

  return (
    <>
      <ImageNameTableTd hasBottomBorder={hasBottomBorder}>
        <ImageWrapper to={url}>
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
        </ImageWrapper>
        <NameInputWrapper>
          <NameLink to={url}>
            <Name>{name}</Name>
          </NameLink>
          <CustomNrStock
            customNr={currentOrdernr}
            stockReadable={availText}
            textColor={color}
            unit={unit}
            availText={availText}
            storesInfo={storesInfo}
          />
        </NameInputWrapper>
      </ImageNameTableTd>
      <StyledTableTd hasBottomBorder={hasBottomBorder} align="right">
        <Price>{perUnitPriceString}</Price>
      </StyledTableTd>
      <StyledTableTd hasBottomBorder={hasBottomBorder}>
        <CountWrapper>
          <CountSetter
            title={
              (count * 10000) % (step * 10000) !== 0
                ? 'Neobjednávate celé balenie.'
                : null
            }
            backgroundColor={
              (count * 10000) % (step * 10000) !== 0 ? '#FFA454' : null
            }
            handleInputFocus={handleInputFocus}
            handleInputBlur={handleInputBlur}
            onChange={handleCountChange}
            unit={unit}
            count={count}
            step={step}
            marginToUnits={unit === 'ks' ? 16 : 0}
          />
        </CountWrapper>
      </StyledTableTd>
      <StyledTableTd hasBottomBorder={hasBottomBorder}>
        <Package>
          {productPackages > 0 ? `${productPackages} ${unit}` : ''}
        </Package>
      </StyledTableTd>
      <StyledTableTd hasBottomBorder={hasBottomBorder}>
        <Package>{priceString}</Package>
      </StyledTableTd>
      <StyledTableTd hasBottomBorder={hasBottomBorder} align="right">
        <SvgIcon
          onClick={handleRemoveClick}
          width={16}
          height={16}
          icon={IconType.trash}
          marginLeft={16}
          marginRight={8}
          viewBox="0 0 16 16"
          alt="Odstrániť produkt"
          title="Odstrániť produkt"
          fill={theme.colors.textPrimary}
          cursor={'pointer'}
        />
      </StyledTableTd>
    </>
  );
}

const StyledTableTd = styled(TableTd)<{ hasBottomBorder: boolean }>`
  padding: ${rem(16)} 0;
  margin-left: ${rem(8)};
  border-bottom: ${({ hasBottomBorder, theme }) =>
    hasBottomBorder ? theme.borders.primary : 'none'};
`;

const CountWrapper = styled(FlexRowCenterHorizontal)`
  margin-left: ${rem(40)};
  align-items: center;
`;

const ImageNameTableTd = styled(StyledTableTd)`
  display: flex;
  align-items: center;
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)``;

const NameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(4)};

  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondaryLight};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(400)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(200)};
  `}
`;

const Price = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-left: ${rem(8)};
`;

const Package = styled(Price)`
  margin-left: 0;
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const WithTheme: any = withTheme(ItemsTabRow);
export { WithTheme as ItemsTabRow, StyledTableRow };
